import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Ship, Plus, Edit, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const BalsasPage = () => {
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBalsa, setEditingBalsa] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        nome: '',
        porte_bruto: '',
        capacidade: '',
        status: 'ativa',
        observacoes: ''
    });

    useEffect(() => {
        fetchBalsas();
    }, []);

    const fetchBalsas = async () => {
        try {
            const response = await axios.get(`${API}/balsas`);
            setBalsas(response.data);
        } catch (error) {
            toast.error('Erro ao carregar balsas');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            ...formData,
            porte_bruto: formData.porte_bruto ? parseFloat(formData.porte_bruto) : null,
            capacidade: formData.capacidade ? parseFloat(formData.capacidade) : null
        };
        
        try {
            if (editingBalsa) {
                await axios.put(`${API}/balsas/${editingBalsa.id}`, data);
                toast.success('Balsa atualizada!');
            } else {
                await axios.post(`${API}/balsas`, data);
                toast.success('Balsa cadastrada!');
            }
            setDialogOpen(false);
            resetForm();
            fetchBalsas();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar balsa');
        }
    };

    const handleEdit = (balsa) => {
        setEditingBalsa(balsa);
        setFormData({
            nome: balsa.nome,
            porte_bruto: balsa.porte_bruto?.toString() || '',
            capacidade: balsa.capacidade?.toString() || '',
            status: balsa.status,
            observacoes: balsa.observacoes || ''
        });
        setDialogOpen(true);
    };

    const handleDelete = async (balsa) => {
        if (!window.confirm(`Deseja remover a balsa "${balsa.nome}"?`)) return;
        
        try {
            await axios.delete(`${API}/balsas/${balsa.id}`);
            toast.success('Balsa removida!');
            fetchBalsas();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao remover balsa');
        }
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            porte_bruto: '',
            capacidade: '',
            status: 'ativa',
            observacoes: ''
        });
        setEditingBalsa(null);
    };

    const canEdit = hasRole(['admin', 'gestor']);
    const canDelete = hasRole(['admin']);

    // Filtrar balsas com base na busca
    const filteredBalsas = balsas.filter(balsa => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            balsa.nome?.toLowerCase().includes(term) ||
            balsa.status?.toLowerCase().includes(term) ||
            balsa.observacoes?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="balsas-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Cadastro de Balsas
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de balsas para composição de comboios
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar balsas..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-balsas"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-balsas"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-balsa"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nova Balsa
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingBalsa ? 'Editar Balsa' : 'Nova Balsa'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome da Balsa *
                                        </Label>
                                    <Input
                                        data-testid="input-balsa-nome"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                        required
                                        placeholder="Ex: ACARI"
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Porte Bruto (t)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.porte_bruto}
                                            onChange={(e) => setFormData({...formData, porte_bruto: e.target.value})}
                                            placeholder="0.00"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Capacidade (m³)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.capacidade}
                                            onChange={(e) => setFormData({...formData, capacidade: e.target.value})}
                                            placeholder="0.00"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Status
                                    </Label>
                                    <Select 
                                        value={formData.status} 
                                        onValueChange={(value) => setFormData({...formData, status: value})}
                                    >
                                        <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            <SelectItem value="ativa">Ativa</SelectItem>
                                            <SelectItem value="inativa">Inativa</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Observações adicionais..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-balsa"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingBalsa ? 'Atualizar' : 'Cadastrar'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {filteredBalsas.length === 0 ? (
                        <div className="p-12 text-center">
                            <Ship className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">
                                {searchTerm ? 'Nenhuma balsa encontrada para a busca' : 'Nenhuma balsa cadastrada'}
                            </p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Porte Bruto</TableHead>
                                    <TableHead>Capacidade</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canEdit && <TableHead>Ações</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredBalsas.map((balsa) => (
                                    <TableRow key={balsa.id} data-testid={`balsa-row-${balsa.id}`}>
                                        <TableCell className="text-white font-medium">
                                            {balsa.nome}
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono">
                                            {balsa.porte_bruto ? `${balsa.porte_bruto} t` : '-'}
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono">
                                            {balsa.capacidade ? `${balsa.capacidade} m³` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${balsa.status === 'ativa' ? 'badge-success' : 'badge-danger'}
                                            `}>
                                                {balsa.status}
                                            </span>
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(balsa)}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    {canDelete && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(balsa)}
                                                            className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                            data-testid={`btn-delete-balsa-${balsa.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default BalsasPage;
